import React, { useState } from "react";
import { Share2, Copy, Check, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * SampleShareCard - lets a guest or member share their completed SAMPLE
 * interview result via a public 30-day link (/s/{share_id}).
 */
export default function SampleShareCard({ sessionId }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const create = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/video-interview/${sessionId}/sample-share`);
      setLink(`${window.location.origin}/s/${data.share_id}`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(link);
      ok = true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = link;
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setError("Couldn't copy automatically. Select the link text and copy it manually.");
    }
  };

  const nativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: "My Intuiterview sample interview result", url: link }).catch(() => {});
    } else {
      copy();
    }
  };

  return (
    <div data-testid="sample-share-card" className="max-w-3xl mx-auto mb-6 rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="h-9 w-9 rounded-full border border-amber-500/30 bg-amber-500/[0.08] flex items-center justify-center flex-shrink-0">
            <Share2 className="h-4 w-4 text-amber-300" />
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-100">Share your result</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Show friends how you did. They can try their own free sample interview.
            </div>
          </div>
        </div>
        {!link && (
          <button
            type="button"
            data-testid="sample-share-create-btn"
            onClick={create}
            disabled={busy}
            className="w-full sm:w-auto justify-center inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-semibold px-4 py-2 transition-colors disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
            Create share link
          </button>
        )}
      </div>
      {error && <div data-testid="sample-share-error" className="mt-3 text-xs text-rose-400">{error}</div>}
      {link && (
        <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div data-testid="sample-share-link" className="flex-1 min-w-0 truncate rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-xs text-zinc-300">
            {link}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="sample-share-copy-btn"
              onClick={copy}
              className="flex-1 sm:flex-none justify-center inline-flex items-center gap-1.5 rounded-full border border-zinc-700 hover:border-zinc-500 text-zinc-200 text-xs font-semibold px-3.5 py-2 transition-colors"
            >
              {copied ? <><Check className="h-3.5 w-3.5 text-emerald-400" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
            </button>
            <button
              type="button"
              data-testid="sample-share-native-btn"
              onClick={nativeShare}
              className="flex-1 sm:flex-none justify-center inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs font-semibold px-3.5 py-2 transition-colors"
            >
              <Share2 className="h-3.5 w-3.5" /> Share
            </button>
          </div>
        </div>
      )}
      {link && <div className="mt-2 text-[10px] text-zinc-600">Link shows your score, the sample role, and your top strengths. Expires in 30 days.</div>}
    </div>
  );
}
